// Pure helpers for the Outreach screen. Display and filtering only — the ranking, the sentences and the
// bands all come from the API (ADR 0003 §4: no business math in the browser).
import type { ExclusionKind, OutreachExclusion, OutreachItem, OutreachView } from '../types/outreach'

/** The filter above the queue: a reason, or everything. */
export type ReasonFilter = 'all' | string

export function filterQueue(items: OutreachItem[], reason: ReasonFilter): OutreachItem[] {
  return reason === 'all' ? items : items.filter((i) => i.reasonKind === reason)
}

/**
 * What the empty list MEANS, in the person's words. The difference between "nothing is owed" and
 * "Scout has not read your mail yet" is the difference between good news and a broken product, and the
 * screen has to say which (owner: empty states honest).
 */
export function emptyState(view: OutreachView, reason: ReasonFilter): { title: string; detail: string } | null {
  if (filterQueue(view.items, reason).length > 0) return null
  if (!view.mailboxConnected)
    return {
      title: 'No one to contact yet — your mailbox is still being read',
      detail: 'Connect your mailbox and Scout reads who you write to, how often, and what you said last. Until then there is nothing to rank.',
    }
  if (view.people === 0)
    return {
      title: 'No one to contact yet — your mailbox is still being read',
      detail: 'Your mailbox is connected but nobody has come out of it yet. The first read is still running; this list fills in as it lands.',
    }
  if (reason !== 'all') {
    const chip = view.counts.find((c) => c.kind === reason)
    return {
      title: `Nothing under "${chip?.label ?? reason}" today`,
      detail: 'Clear the filter to see the rest of the queue.',
    }
  }
  return {
    title: 'Nothing owed today',
    detail: 'Everyone on a cadence has been contacted inside it, nobody is waiting on you, and nothing has gone quiet. Open "not listed, and why" to see who that covers.',
  }
}

/** The line under the header when Scout can only see half the correspondence. Null once the Inbox lands. */
export function inboxCaveat(view: OutreachView): string | null {
  return view.inboundOnFile
    ? null
    : 'Scout has read your sent mail, not your Inbox, so it cannot see who wrote to you and got no reply. Two of the six reasons below are switched off until that read lands — nothing here is claiming they never wrote.'
}

/**
 * What the night left ready, in one line: how many people on the list have a brief on file and when the
 * newest of them was written. Null when none has been written yet — an empty promise is worse than silence
 * (the API counts; the browser only says it).
 */
export function preparedLine(view: OutreachView, now: Date = new Date()): string | null {
  const p = view.prepared
  if (!p || p.people === 0 || !p.at) return null
  const at = new Date(p.at)
  if (Number.isNaN(at.getTime())) return null
  const sameDay = at.toDateString() === now.toDateString()
  const stamp = sameDay
    ? at.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${p.people} ${p.people === 1 ? 'brief is' : 'briefs are'} ready, newest written ${stamp}. Each of those rows carries the opening line the brief suggested, and what earns it.`
}

/** Exclusions grouped by kind, in the API's order, for the collapsed "not listed, and why" section. */
export function groupExclusions(excluded: OutreachExclusion[]): { kind: ExclusionKind | string; label: string; people: OutreachExclusion[] }[] {
  const order: string[] = []
  const byKind = new Map<string, OutreachExclusion[]>()
  for (const e of excluded) {
    if (!byKind.has(e.kind)) { byKind.set(e.kind, []); order.push(e.kind) }
    byKind.get(e.kind)!.push(e)
  }
  return order.map((kind) => ({ kind, label: exclusionLabel(kind), people: byKind.get(kind)! }))
}

export function exclusionLabel(kind: string): string {
  switch (kind) {
    case 'colleague': return 'Your own colleagues'
    case 'vendor': return 'People who sell to the firm'
    case 'competitor': return 'Rivals'
    case 'automated': return 'Machines'
    case 'no-address': return 'No address to write to'
    case 'dismissed': return 'You took them off the list'
    case 'contacted': return 'Contacted inside their cadence'
    default: return kind
  }
}

/** Which way a history line went, in words. */
export function directionWords(direction: string): string {
  switch (direction) {
    case 'own': return 'You wrote'
    case 'inbound': return 'They wrote'
    case 'meeting': return 'You met'
    default: return direction
  }
}

/** The plain sentence for "do I really know this person?" — the API's verdict, as a heading. */
export function acquaintanceTitle(verdict: string): string {
  switch (verdict) {
    case 'known': return 'You know this person'
    case 'one-way': return 'One way only, on the record'
    case 'imported': return "You may not know this person"
    default: return verdict
  }
}

/** How many rows are showing, given the batch size and how many times "show five more" was pressed. */
export function shown(total: number, batch: number, more: number): number {
  return Math.min(total, batch * (more + 1))
}
